<template>
  <v-card
    v-if="item && Object.keys(item).length > 0"
    flat
    class="printableArea font my-0"
    id="printBill"
    :outlined="outlined"
  >
    <v-card-text class="pa-0">
      <div class="a4-02-container pa-4">
        <!-- Top Corner Marks removed -->

        <!-- Header Section -->
        <v-row no-gutters class="mb-4">
          <!-- Logo & Company Info (Left) -->
          <v-col cols="3" class="text-left">
            <div
              v-if="Img && show_logo"
              class="logo-box d-flex align-center justify-start mb-2"
            >
              <img
                :src="Img"
                alt="Company Logo"
                style="width: 4cm; height: 4cm; object-fit: contain"
              />
            </div>
          </v-col>

          <!-- Store Name (Center) -->
          <v-col cols="6" class="text-center justify-center d-flex">
            <div class="text-center">
              <h2
                class="font-weight-bold text-h5"
                v-html="safeItem?.store_id?.name || $t('store_name')"
              ></h2>
              <div v-if="phoneMobileLine" class="font default">
                {{ phoneMobileLine }}
              </div>
            </div>
          </v-col>

          <!-- Empty Right Column (to maintain centering) -->
          <v-col cols="3"> </v-col>
          <!-- company section -->
          <!-- Company Section (Left) -->
          <v-col cols="6">
            <div class="company-info font default">
              <div v-if="show_store_address" class="d-flex align-start">
                <span class="mr-1 font-weight-bold">
                  ທີ່ຢູ່
                  {{ $t("company_address") }}:</span
                >
                <div v-html="safeItem?.store_id?.address"></div>
              </div>
              <div class="mt-1" v-if="safeItem?.store_id?.desc">
                <span class="font-weight-bold">ວີແຊັດ {{ $t("wechat") }}:</span>
                {{ safeItem.store_id.desc }}
              </div>
              <div class="mt-1" v-if="show_email && user?.email">
                <span class="font-weight-bold">ອີເມວ {{ $t("email") }}:</span>
                {{ user.email }}
              </div>
              <div class="mt-1">
                <span class="font-weight-bold">ວັນທີ {{ $t("date") }}:</span>
                {{ safeItem?.created_at | yyyymmdd }}
              </div>
            </div>
          </v-col>

          <!-- Customer Section (Right) -->
          <v-col cols="6" class="text-right">
            <div class="customer-info font default pt-4">
              <div class="d-flex justify-end">
                <span class="mr-1 font-weight-bold"
                  >ຊື່ລູກຄ້າ {{ $t("customer_name") }}:</span
                >
                <span v-if="safeItem?.customer_id">{{ customer }}</span>
                <span v-else>________________</span>
              </div>
              <div class="d-flex justify-end mt-1">
                <span class="mr-1 font-weight-bold"
                  >ທີ່ຢູ່ {{ $t("address") }}:</span
                >
                <span v-if="safeItem?.customer_id">{{ address || "–" }}</span>
                <span v-else>________________</span>
              </div>
              <div class="d-flex justify-end mt-1">
                <span class="mr-1 font-weight-bold"
                  >ເບີໂທ {{ $t("phone_no") }}:</span
                >
                <span v-if="safeItem?.customer_id">{{ customerPhone }}</span>
                <span v-else>________________</span>
              </div>
            </div>
          </v-col>
        </v-row>

        <div class="text-center my-10 font-weight-bold">
          <h1 class="text-h4 mb-4">ໃບຮ້ບເງີນ</h1>
          <h1 class="text-h4">{{ $t("sales_order") }}</h1>
        </div>

        <!-- Order No Above Table -->
        <div class="d-flex justify-end mb-1 font default mb-8">
          <span class="font-weight-bold">ເລກທີ {{ $t("order_no") }}:</span>
          <span class="ml-2">{{ safeItem?.code || "" }}</span>
        </div>

        <div v-if="queueLabel" class="d-flex justify-end mb-2 font">
          <span class="font-weight-bold">{{ $t("queue_number") }}:</span>
          <span class="ml-2 font-weight-black" style="font-size: 28px">
            {{ queueLabel }}
          </span>
        </div>

        <table class="a4-02-table w-100 mb-4">
          <thead>
            <tr>
              <th class="text-center" :style="{ width: colWidths.no }">
                ລຳດັບ <br />
                {{ $t("no") }}
              </th>

              <th class="text-left" :style="{ width: colWidths.category }">
                ປະເພດສິນຄ້າ <br />
                {{ $t("category") }}
              </th>
              <th class="text-left" :style="{ width: colWidths.name }">
                ລາຍການສິນຄ້າ <br />
                {{ $t("product_name") }}
              </th>
              <!-- Dynamic variant columns -->
              <template v-if="hasVariants">
                <th
                  v-for="header in variantHeaders"
                  :key="header"
                  class="text-left"
                  :style="{ width: colWidths.variant }"
                >
                  {{ getLaoHeader(header) }} <br />
                  {{ header === '颜色' ? '颜色' : $t(header.toLowerCase()) }}
                </th>
              </template>

              <th class="text-center" :style="{ width: colWidths.spec }">
                ລາຍລະອຽດ<br />
                {{ $t("specification") }}
              </th>
              <th class="text-center" :style="{ width: colWidths.pcs }">
                ຈຳນວນແກັດ<br />
                {{ $t("pcs") }}
              </th>
              <th class="text-center" :style="{ width: colWidths.qty }">
                ຈຳນວນ/ອັນ <br />
                {{ $t("quantity") }}
              </th>
              <th class="text-right" :style="{ width: colWidths.price }">
                ລາຄາ <br />
                {{ $t("price") }}
              </th>
              <th class="text-right" :style="{ width: colWidths.total }">
                ລວມ<br />
                {{ $t("total") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(data, index) in safeItem?.history || []" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td class="text-left">
                {{ getCategoryName(data.product?.category) }}
              </td>
              <td class="text-left">
                {{ data.product?.name || $t("product_name") }}
                <span
                  v-if="isStampRewardLine(safeItem, data)"
                  class="font stamp-reward-tag"
                >
                  ({{ $t("stamp_paid_by_stamp") }})
                </span>
                <div
                  v-if="data.bundle_stock_logs && data.bundle_stock_logs.length"
                >
                  <div
                    v-for="(bundle, bIdx) in data.bundle_stock_logs"
                    :key="`b-${index}-${bIdx}`"
                    class="text-caption"
                  >
                    - {{ bundle.bundle_item_product.name }} x
                    {{ bundle.bundle_qty }}
                  </div>
                </div>
              </td>
              <!-- Dynamic variant cells -->
              <template v-if="hasVariants">
                <td
                  v-for="header in variantHeaders"
                  :key="`${index}-${header || '__empty__'}`"
                  class="text-left"
                >
                  {{ getVariantValue(data, header) }}
                </td>
              </template>
              <td class="text-center">{{ data.product?.desc }}</td>
              <td class="text-center">
                <template v-if="data.unit_amount">
                  {{ data.qty }}×{{ data.unit_amount }}
                </template>
                <template v-else>
                  {{ data.qty | ToNumber }}
                </template>
              </td>
              <td class="text-center">{{ (data.qty * (data.unit_amount || 1)) | ToNumber }}</td>
              <td class="text-right">
                <template v-if="isStampRewardLine(safeItem, data)">
                  {{ $t("free") }}
                </template>
                <template v-else>
                  {{ data.price_sale | NumberFormat }}
                </template>
              </td>
              <td class="text-right">
                <template v-if="isStampRewardLine(safeItem, data)">
                  {{ $t("free") }}
                </template>
                <template v-else>
                  {{ data.total_price | NumberFormat }}
                </template>
              </td>
            </tr>
            <template v-if="hasBuffetPlans">
              <tr>
                <td
                  :colspan="tableBodyColumnCount"
                  class="text-left font-weight-bold"
                >
                  {{ $t("buffet_plans") }} ({{ $t("buffet_persons") }}
                  {{ buffetTotalPersons }})
                </td>
              </tr>
              <tr
                v-for="(plan, pidx) in receiptBuffetPlans"
                :key="`bp-${pidx}`"
              >
                <td class="text-center">
                  {{ (safeItem.history || []).length + pidx + 1 }}
                </td>
                <td></td>
                <td class="text-left">{{ plan.plan_name }}</td>
                <template v-if="hasVariants">
                  <td
                    v-for="header in variantHeaders"
                    :key="`bpv-${pidx}-${header}`"
                  ></td>
                </template>
                <td></td>
                <td></td>
                <td class="text-center">{{ plan.persons | ToNumber }}</td>
                <td class="text-right">{{ plan.unit_price | NumberFormat }}</td>
                <td class="text-right">{{ plan.subtotal | NumberFormat }}</td>
              </tr>
            </template>
          </tbody>
        </table>

        <!-- Summary Section (As a separate table for layout stability) -->
        <div class="d-flex justify-end mb-4 font default">
          <table style="border: none !important; border-collapse: collapse">
            <!-- Subtotal -->
            <tr v-if="subtotal">
              <td
                class="text-right font-weight-bold text-uppercase"
                style="border: none !important; padding: 4px 40px 4px 0"
              >
                ລວມເງີນບາດ {{ $t("subtotal_thb") }}
              </td>
              <td
                class="text-right"
                style="border: none !important; width: 135px; padding: 4px 0"
              >
                {{ subtotal }}
              </td>
            </tr>

            <!-- Discount -->
            <tr v-if="safeItem?.total_discount">
              <td
                class="text-right font-weight-bold text-uppercase"
                style="border: none !important; padding: 4px 40px 4px 0"
              >
                ສ່ວນຫຼຸດ {{ $t("discount") }}
              </td>
              <td
                class="text-right"
                style="border: none !important; width: 135px; padding: 4px 0"
              >
                {{ total_discount }}
              </td>
            </tr>

            <!-- VAT -->
            <tr v-if="tax_include && safeItem?.tax_amount">
              <td
                class="text-right font-weight-bold text-uppercase"
                style="border: none !important; padding: 4px 40px 4px 0"
              >
                ພາສີ {{ $t("vat") }} {{ tax }}
              </td>
              <td
                class="text-right"
                style="border: none !important; width: 135px; padding: 4px 0"
              >
                {{ safeItem?.tax_amount | NumberFormat }}
              </td>
            </tr>

            <!-- Grand Total -->
            <tr class="font-weight-bold">
              <td
                class="text-right text-uppercase"
                style="border: none !important; padding: 4px 40px 4px 0"
              >
                ລວມເງີນກີບ {{ $t("grand_total_lak") }}
              </td>
              <td
                class="text-right"
                style="border: none !important; width: 135px; padding: 4px 0"
              >
                {{ grand_total }}
              </td>
            </tr>

            <!-- Exchange Rate Section -->
            <template>
              <tr class="font-weight-bold">
                <td
                  class="text-right text-uppercase"
                  style="border: none !important; padding: 4px 40px 4px 0"
                >
                  ອັດຕາແລກປ່ຽນ {{ $t("exchange_rate") }}
                </td>
                <td
                  class="text-right"
                  style="border: none !important; width: 135px; padding: 4px 0"
                >
                  {{ exchangeRate }}
                </td>
              </tr>
            </template>
          </table>
        </div>

        <!-- Footer Info -->
        <div class="footer-info mb-6 font default">
          <div
            class="mb-1"
            v-if="show_bank_account && displayAccounts.length > 0"
          >
            <strong>ບັນຊີຮັບເງິນ {{ $t("receiving_account") }}:</strong>
            <div
              v-for="(account, index) in displayAccounts"
              :key="index"
              class="pl-4 mt-1"
            >
              {{ account.bank_name }}: {{ account.account_no }}
              {{ account.account_name }}
            </div>
          </div>
        </div>

        <!-- Amount Letter -->
        <v-row v-if="show_amount_letter">
          <v-col cols="12">
            <div class="font default">
              <strong>{{ $t("amount_letter") }}:</strong>
              <span class="font-weight-bold">
                {{ grand_total_number | numberToLetter }}
              </span>
              {{ paymentCurrency }}
            </div>
          </v-col>
        </v-row>

        <!-- Signatures -->
        <div class="mt-10 pa-4 font default">
          <v-row no-gutters>
            <v-col cols="4">
              <div class="mb-4">ຜູ້ອອກບິນ {{ $t("issuer") }}:</div>
              <div>ວັນທີ {{ $t("date") }} :</div>
            </v-col>
            <v-col cols="4">
              <div class="mb-4">
                ລາຍເຊັນຄົນຂັບ {{ $t("driver_signature") }} :
              </div>
              <div>ວັນທີ {{ $t("date") }} :</div>
            </v-col>
            <v-col cols="4">
              <div class="mb-4">
                ລາຍເຊັນລູກຄ້າ {{ $t("customer_signature") }} :
              </div>
              <div>ວັນທີ {{ $t("date") }} :</div>
            </v-col>
          </v-row>
        </div>

        <!-- Bottom Corner Marks removed -->
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { myMixin } from "@/mixin/main";
import { formatBillQueue } from "@/utils/billQueue";

export default {
  name: "AppReceiptA4_02",
  mixins: [myMixin],
  props: {
    item: Object,
    show_logo: Boolean,
    branch: Boolean,
    customer_point: Boolean,
    customer_phone: Boolean,
    customer_address: Boolean,
    thb_currency: Boolean,
    usd_currency: Boolean,
    tax_include: Boolean,
    show_qr: Boolean,
    footer_text: String,
    customer_logistics: Boolean,
    outlined: {
      type: Boolean,
      default: false,
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    show_phone_number: {
      type: Boolean,
      default: true,
    },
    show_mobile_number: {
      type: Boolean,
      default: true,
    },
    show_store_address: {
      type: Boolean,
      default: true,
    },
    show_payment_method: {
      type: Boolean,
      default: false,
    },
    show_email: {
      type: Boolean,
      default: true,
    },
    show_bank_account: {
      type: Boolean,
      default: true,
    },
    show_amount_letter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      accountsData: [],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    queueLabel() {
      return formatBillQueue(this.item?.queue_no);
    },
    safeItem() {
      if (!this.item) return null;
      if (!this.item.store_id) {
        this.item.store_id = { name: this.$t("store_name") };
      }
      if (!this.item.user_id) {
        this.item.user_id = { first_name: this.$t("unknown") };
      }
      if (this.item.history) {
        this.item.history = this.item.history.map((historyItem) => {
          if (!historyItem.product_id) {
            historyItem.product_id = { name: this.$t("product_name") };
          }
          return historyItem;
        });
      }
      return this.item;
    },
    storePhone() {
      if (this.safeItem?.store_id?.phone_no) {
        return this.safeItem.store_id.phone_no;
      }
      return "";
    },
    storeMobile() {
      if (this.safeItem?.store_id?.mobile_no) {
        return this.safeItem.store_id.mobile_no;
      }
      return "";
    },
    phoneMobileLine() {
      const parts = [];
      if (this.show_phone_number && this.storePhone) {
        parts.push(this.storePhone);
      }
      if (this.show_mobile_number && this.storeMobile) {
        parts.push(this.storeMobile);
      }
      return parts.join(" / ");
    },
    receiptBuffetPlans() {
      const r = this.safeItem;
      if (!r) return [];
      if (Array.isArray(r.buffet_plans) && r.buffet_plans.length) {
        return r.buffet_plans;
      }
      if (Array.isArray(r.buffetPlans) && r.buffetPlans.length) {
        return r.buffetPlans;
      }
      return [];
    },
    hasBuffetPlans() {
      return this.receiptBuffetPlans.length > 0;
    },
    buffetTotalPersons() {
      return this.receiptBuffetPlans.reduce(
        (sum, p) => sum + (Number(p.persons) || 0),
        0
      );
    },
    tableBodyColumnCount() {
      return 8 + this.variantHeaders.length;
    },
    getProductDisplayName() {
      return (data) => {
        if (!data || !data.product_id) {
          return this.$t("product_name");
        }
        let productName = data.product?.name || this.$t("product_name");
        if (data.product_variant) {
          let options = [];
          if (
            data.product_variant.optionValues &&
            typeof data.product_variant.optionValues === "object"
          ) {
            options = Object.values(data.product_variant.optionValues);
          } else if (
            data.product_variant.options &&
            Array.isArray(data.product_variant.options)
          ) {
            options = data.product_variant.options.map(
              (option) => option.selected_value
            );
          }
          if (options.length > 0) {
            productName = `${productName} (${options.join(", ")})`;
          }
        }
        return productName;
      };
    },
    getSpecification() {
      return (data) => {
        if (data.product?.size?.name) {
          return data.product.size.name;
        }
        if (data.product_variant) {
          let options = [];
          if (
            data.product_variant.optionValues &&
            typeof data.product_variant.optionValues === "object"
          ) {
            options = Object.values(data.product_variant.optionValues);
          } else if (
            data.product_variant.options &&
            Array.isArray(data.product_variant.options)
          ) {
            options = data.product_variant.options.map(
              (option) => option.selected_value
            );
          }
          if (options.length > 0) {
            return options.join(", ");
          }
        }
        return "-";
      };
    },
    getUnit() {
      return (data) => {
        if (data.product?.size?.amount) {
          return this.$options.filters.NumberFormat(data.product.size.amount);
        }
        return "1";
      };
    },
    Img() {
      if (this.user && this.user.stores && this.user.stores.logo) {
        if (!this.user.stores.logo.includes("default")) {
          return Help.img(this.user.stores.logo);
        }
      }
      return null;
    },
    customer() {
      if (this.safeItem?.customer_id && this.safeItem.customer_id.first_name) {
        return `${this.safeItem.customer_id.first_name}`;
      }
      return "";
    },
    customerPhone() {
      if (this.safeItem?.customer_id && this.safeItem.customer_id.phone_no) {
        return this.safeItem.customer_id.phone_no;
      }
      return "";
    },
    address() {
      if (
        this.safeItem?.customer_id?.village?.vill_name &&
        this.safeItem?.customer_id?.village?.district?.dr_name &&
        this.safeItem?.customer_id?.village?.district?.province?.pr_name
      ) {
        const village = this.safeItem.customer_id.village;
        const district = village.district;
        const province = district.province;
        return `${village.vill_name}, ${district.dr_name}, ${province.pr_name}`;
      }
      if (this.safeItem?.customer_id?.address) {
        return this.safeItem.customer_id.address;
      }
      return "";
    },
    subtotal() {
      if (this.safeItem && this.safeItem.total_price_sale) {
        // Show the full price before discount
        return this.$options.filters.NumberFormat(
          this.safeItem.total_price_sale
        );
      }
      if (
        this.safeItem &&
        this.safeItem.total_price &&
        this.safeItem.total_discount
      ) {
        return this.$options.filters.NumberFormat(this.safeItem.total_price);
      }
      return 0;
    },
    total_discount() {
      if (this.safeItem && this.safeItem.total_discount) {
        return `- ${this.$options.filters.NumberFormat(
          this.safeItem.total_discount
        )}`;
      }
      return 0;
    },
    tax() {
      if (this.safeItem?.store_id?.tax) {
        return this.safeItem.store_id.tax;
      }
      return 0;
    },
    grand_total() {
      if (this.safeItem && this.safeItem.total_price) {
        const total_price = this.safeItem.total_price;
        const exchange_rate = this.safeItem.store_id.thb;
        const grand_total = total_price * exchange_rate;
        // total_price already includes discount applied (total_price = total_price_sale - total_discount)
        return this.$options.filters.NumberFormat(grand_total);
      }
      return "0";
    },
    grand_total_number() {
      if (this.safeItem && this.safeItem.total_price) {
        return this.safeItem.total_price - (this.safeItem.total_discount || 0);
      }
      return 0;
    },
    itemCurrency() {
      if (
        this.safeItem &&
        this.safeItem.history &&
        this.safeItem.history.length > 0 &&
        this.safeItem.history[0].product &&
        this.safeItem.history[0].product.currency
      ) {
        return this.safeItem.history[0].product.currency.code;
      }
      return this.paymentCurrency;
    },
    paymentCurrency() {
      if (
        this.safeItem &&
        this.safeItem.currency_id &&
        this.safeItem.currency_id.code
      ) {
        return this.safeItem.currency_id.code;
      }
      return "LAK";
    },

    exchangeRate() {
      if (this.safeItem?.store_id) {
        const currencyCode = this.itemCurrency.toLowerCase();
        if (this.safeItem.store_id[currencyCode]) {
          return this.safeItem.store_id[currencyCode];
        }
      }
      return this.safeItem?.exchange_rate || 1;
    },
    totalPaidInPaymentCurrency() {
      if (this.safeItem?.total_paid) {
        return this.safeItem.total_paid;
      }
      if (this.safeItem?.total_price) {
        return this.safeItem.total_price * this.exchangeRate;
      }
      return 0;
    },
    salespersonName() {
      if (this.safeItem?.user_id && this.safeItem.user_id.first_name) {
        return this.safeItem.user_id.first_name;
      }
      return this.$t("unknown");
    },
    displayAccounts() {
      return this.accounts && this.accounts.length > 0
        ? this.accounts
        : this.accountsData;
    },
    colWidths() {
      const vLen = this.variantHeaders.length;
      if (vLen >= 2) {
        return {
          no: "5%",
          category: "10%",
          name: "16%",
          variant: "9%",
          spec: "11%",
          pcs: "11%",
          qty: "9%",
          price: "10%",
          total: "10%",
        };
      }
      if (vLen === 1) {
        return {
          no: "5%",
          category: "11%",
          name: "18%",
          variant: "10%",
          spec: "12%",
          pcs: "12%",
          qty: "10%",
          price: "10%",
          total: "12%",
        };
      }
      return {
        no: "5%",
        category: "13%",
        name: "25%",
        variant: "0%",
        spec: "13%",
        pcs: "12%",
        qty: "10%",
        price: "10%",
        total: "12%",
      };
    },
    variantHeaders() {
      const headers = new Set();
      if (this.safeItem?.history) {
        this.safeItem.history.forEach((item) => {
          const optValues =
            item.product_variant?.optionValues ||
            item.productVariant?.optionValues;
          if (optValues && typeof optValues === "object") {
            Object.keys(optValues).forEach((key) => {
              let trimmed = (key || "").trim();
              if (
                !trimmed ||
                trimmed.toLowerCase() === "color" ||
                trimmed === "ສີ"
              ) {
                trimmed = "颜色";
              }
              headers.add(trimmed);
            });
          }
        });
      }
      return Array.from(headers);
    },
    hasVariants() {
      return this.variantHeaders.length > 0;
    },
    summaryColspan() {
      // Base columns: No, Product Name, Unit, Quantity, Price, Total = 6
      // Plus variant columns (0 or more)
      return 5 + this.variantHeaders.length;
    },
  },
  mounted() {
    if (
      this.show_bank_account &&
      (!this.accounts || this.accounts.length === 0)
    ) {
      this.fetchBankAccount();
    }
  },
  methods: {
    getVariantValue(data, headerName) {
      const optValues =
        data.product_variant?.optionValues ||
        data.productVariant?.optionValues;
      if (!optValues || typeof optValues !== "object") {
        return "-";
      }
      if (
        optValues[headerName] !== undefined &&
        optValues[headerName] !== null &&
        optValues[headerName] !== ""
      ) {
        return optValues[headerName];
      }
      if (headerName === "颜色" || headerName === "color") {
        for (const k of ["颜色", "", "color", "Color", "ສີ"]) {
          if (optValues[k]) return optValues[k];
        }
      }
      return "-";
    },
    getLaoHeader(name) {
      if (!name) return "";
      if (name === "color" || name === "颜色") return "ສີ";
      const key = name.toLowerCase();
      // Try to get Lao translation from i18n messages
      // This assumes this.$i18n.messages.la exists
      try {
        const laMessages = this.$i18n.messages.la;
        if (laMessages && laMessages[key]) {
          return laMessages[key];
        }
      } catch (e) {
        console.warn("Could not fetch Lao messages", e);
      }
      return name;
    },
    async fetchBankAccount() {
      try {
        const { data } = await this.$http.get("/bank_accounts");
        if (data && data.data) {
          this.accountsData = data.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.stamp-reward-tag {
  font-weight: bold;
  margin-left: 4px;
}

.a4-02-table {
  width: 100% !important;
  table-layout: fixed !important;
  border-collapse: collapse !important;
  word-break: break-word;
  overflow-wrap: break-word;
}

.a4-02-table th,
.a4-02-table td {
  border: 1px solid #ddd;
  padding: 4px 6px;
  vertical-align: top;
  word-break: break-word;
  overflow-wrap: break-word;
}

.a4-02-table th {
  background-color: #f9f9f9;
  font-weight: bold;
  font-size: 12px !important;
}

@media print {
  @page {
    size: A4 portrait;
    margin: 8mm;
  }

  .a4-02-container {
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .a4-02-table {
    width: 100% !important;
    table-layout: fixed !important;
  }

  .a4-02-table th,
  .a4-02-table td {
    border: 1px solid #000 !important;
  }
}
</style>
